import React, { useState, useEffect, useCallback } from 'react';
import MetricCard from './MetricCard';
import ChartWithTableToggle from './ChartWithTableToggle';
import { TimeRange, DashboardData } from '../../types/analytics';

interface DashboardMetricsProps {
  timeRange: TimeRange;
  apiBaseUrl: string;
  project: string;
  mode?: 'hive' | 'iceberg';
  timeTravelTimestamp?: string | null;
}

const DashboardMetrics: React.FC<DashboardMetricsProps> = ({
  timeRange,
  apiBaseUrl,
  project,
  mode = 'hive',
  timeTravelTimestamp = null,
}) => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [queryExecutionTime, setQueryExecutionTime] = useState<number | null>(null);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setDashboardData(null); // Clear old data to show loading state

      const startTime = Date.now();

      // Build params based on whether we have date range or single date
      const params = new URLSearchParams({ project, mode });

      if (timeRange.startDate && timeRange.endDate) {
        // Date range mode
        params.append('start_date', timeRange.startDate);
        params.append('end_date', timeRange.endDate);
      } else if (timeRange.year && timeRange.month && timeRange.day) {
        // Backward compatibility: single date mode
        params.append('year', timeRange.year);
        params.append('month', timeRange.month);
        params.append('day', timeRange.day);
      } else {
        throw new Error('Invalid time range: must provide either start_date/end_date or year/month/day');
      }

      // Add time travel timestamp if provided (Iceberg mode only)
      if (timeTravelTimestamp && mode === 'iceberg') {
        // Convert local time to UTC for Iceberg time travel
        // datetime-local gives us "2026-03-15T17:19" in local time
        // We need to convert to UTC ISO format for Iceberg
        const localDate = new Date(timeTravelTimestamp);
        const utcTimestamp = localDate.toISOString().replace('Z', ''); // Remove Z to keep: 2026-03-15T22:19:00.000
        params.append('as_of_timestamp', utcTimestamp);
      }

      const response = await fetch(`${apiBaseUrl}/analytics/dashboard?${params}`);

      if (!response.ok) {
        throw new Error(`Dashboard fetch failed: ${response.statusText}`);
      }

      const data: DashboardData = await response.json();
      const endTime = Date.now();
      setQueryExecutionTime(endTime - startTime);
      setDashboardData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [timeRange, apiBaseUrl, project, mode, timeTravelTimestamp]);

  // Fetch only on mount and when time range changes (event-driven, no auto-refresh)
  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (error) {
    return (
      <div className="bg-red-900 bg-opacity-30 border border-red-700 rounded-lg p-6">
        <p className="text-red-300">{error}</p>
        <button
          onClick={fetchDashboard}
          className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Format date range for display
  const dateRangeDisplay = timeRange.startDate && timeRange.endDate
    ? timeRange.startDate === timeRange.endDate
      ? timeRange.startDate
      : `${timeRange.startDate} to ${timeRange.endDate}`
    : timeRange.year && timeRange.month && timeRange.day
    ? `${timeRange.year}-${timeRange.month}-${timeRange.day}`
    : 'selected range';

  return (
    <div className="space-y-6 relative">
      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-gray-900 bg-opacity-75 z-50 flex items-start justify-center pt-12 rounded-lg">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 max-w-md shadow-2xl">
            <div className="flex items-center gap-4">
              <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <div>
                <h3 className="text-lg font-semibold text-gray-100">Loading Analytics</h3>
                <p className="text-sm text-gray-400 mt-1">Querying data for {dateRangeDisplay}</p>
                <p className="text-xs text-gray-500 mt-2">This may take a few moments for larger date ranges...</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Refresh Button and Query Stats */}
      <div className="flex justify-end items-center gap-4">
        {/* Query Execution Time Badge */}
        {queryExecutionTime !== null && !loading && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5">
            <div className="flex items-center gap-2 text-sm">
              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-gray-400">Query time:</span>
              <span className="text-blue-400 font-semibold">{queryExecutionTime}ms</span>
            </div>
          </div>
        )}

        <button
          onClick={fetchDashboard}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors text-sm font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>
      {/* KPI Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {project === 'pvq' ? (
          <>
            <MetricCard
              title="Total Interactions"
              value={dashboardData?.total_interactions?.toLocaleString() || '0'}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              }
              loading={loading}
            />

            <MetricCard
              title="Avg Response Time"
              value={dashboardData ? `${dashboardData.avg_response_time?.toFixed(0) || 0}ms` : '0ms'}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
              loading={loading}
            />

            <MetricCard
              title="Block Rate"
              value={dashboardData ? `${dashboardData.block_rate?.toFixed(1) || 0}%` : '0%'}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                  />
                </svg>
              }
              loading={loading}
            />

            <MetricCard
              title="User Satisfaction"
              value={dashboardData ? `${dashboardData.avg_satisfaction?.toFixed(1) || 0}/5` : '0/5'}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
              loading={loading}
            />
          </>
        ) : (
          <>
            <MetricCard
              title="Total Designations"
              value={dashboardData?.total_designations?.toLocaleString() || '0'}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              }
              loading={loading}
            />

            <MetricCard
              title="Avg Processing Time"
              value={dashboardData ? `${dashboardData.avg_processing_time?.toFixed(0) || 0}ms` : '0ms'}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
              loading={loading}
            />

            <MetricCard
              title="Critical-Sensitive Rate"
              value={dashboardData ? `${dashboardData.critical_sensitive_rate?.toFixed(1) || 0}%` : '0%'}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              }
              loading={loading}
            />

            <MetricCard
              title="High Risk Rate"
              value={dashboardData ? `${dashboardData.high_risk_rate?.toFixed(1) || 0}%` : '0%'}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              }
              loading={loading}
            />
          </>
        )}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {project === 'pvq' ? (
          <>
            {/* Interactions by Type */}
            <ChartWithTableToggle
              title="Interactions by Type"
              chartType="bar"
              data={dashboardData?.interaction_types || []}
              xKey="interaction_type"
              yKey="count"
              loading={loading}
            />

            {/* Hourly Activity */}
            <ChartWithTableToggle
              title="Hourly Activity"
              chartType="line"
              data={dashboardData?.hourly_trends || []}
              xKey="hour"
              yKey="interactions"
              loading={loading}
            />

            {/* Guardrail Actions */}
            <ChartWithTableToggle
              title="Guardrail Actions"
              chartType="pie"
              data={dashboardData?.guardrail_breakdown || []}
              xKey="action"
              yKey="count"
              loading={loading}
            />

            {/* Satisfaction Distribution */}
            <ChartWithTableToggle
              title="Satisfaction Distribution"
              chartType="bar"
              data={dashboardData?.satisfaction_distribution || []}
              xKey="rating"
              yKey="count"
              loading={loading}
            />
          </>
        ) : (
          <>
            {/* Sensitivity Breakdown */}
            <ChartWithTableToggle
              title="Sensitivity Breakdown"
              chartType="pie"
              data={dashboardData?.sensitivity_breakdown || []}
              xKey="sensitivity_designation"
              yKey="count"
              loading={loading}
            />

            {/* Risk Breakdown */}
            <ChartWithTableToggle
              title="Risk Breakdown"
              chartType="pie"
              data={dashboardData?.risk_breakdown || []}
              xKey="risk_designation"
              yKey="count"
              loading={loading}
            />

            {/* Investigation Tiers */}
            <ChartWithTableToggle
              title="Investigation Tiers"
              chartType="bar"
              data={dashboardData?.investigation_tiers || []}
              xKey="investigation_tier"
              yKey="count"
              loading={loading}
            />

            {/* Form Types */}
            <ChartWithTableToggle
              title="Form Types"
              chartType="bar"
              data={dashboardData?.form_types || []}
              xKey="form_type"
              yKey="count"
              loading={loading}
            />

            {/* Top Agencies */}
            <ChartWithTableToggle
              title="Top Agencies"
              chartType="bar"
              data={dashboardData?.top_agencies || []}
              xKey="agency"
              yKey="count"
              loading={loading}
            />

            {/* Hourly Trends */}
            <ChartWithTableToggle
              title="Hourly Trends"
              chartType="line"
              data={dashboardData?.hourly_trends || []}
              xKey="hour"
              yKey="designations"
              loading={loading}
            />
          </>
        )}
      </div>

    </div>
  );
};

export default DashboardMetrics;
